import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert, Input, Button, FormGroup, Label, Form } from "reactstrap";
import axiosInstance from "../../request";
import { URL } from "../../api";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 25;

const OverdueReporting = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false); // 👉 State for Excel/CSV loader
  const [error, setError] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loanId, setLoanId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [filterType, setFilterType] = useState("loanId");
  const [filtering, setFiltering] = useState(false);

  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchOverdue(0);
  }, []);

  const fetchOverdue = (pageNo) => {
    setLoading(true);
    setError("");

    const params = {
      page: pageNo,
      size: PAGE_SIZE,
    };

    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (filterType === "loanId" && loanId) params.loanId = loanId;
    if (filterType === "customerId" && customerId) params.customerId = customerId;

    axiosInstance
      .get(URL.OVERDUE_REPORTING, { params })
      .then((res) => {
        const result = Array.isArray(res.data) ? res.data : [];
        setData(result);
        setPage(pageNo);
        setHasNextPage(result.length === PAGE_SIZE);
      })
      .catch(() => {
        setError("Failed to fetch overdue reporting data.");
        setData([]);
      })
      .finally(() => {
        setLoading(false);
        setFiltering(false);
      });
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setFiltering(true);
    fetchOverdue(0);
  };

  const exportToCsv = () => {
    setExporting(true); // 👉 Start loader
    const params = { export: true };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (filterType === "loanId" && loanId) params.loanId = loanId;
    if (filterType === "customerId" && customerId) params.customerId = customerId;

    axiosInstance
      .get(URL.OVERDUE_REPORTING, {
        params,
        responseType: "blob",
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `Overdue_Report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => alert("Failed to export CSV"))
      .finally(() => setExporting(false)); // 👉 Stop loader
  };

  const totals = data.reduce(
    (acc, row) => {
      acc.overdueOutstanding += row.overdue_outstanding || 0;
      acc.penalty += row.penalty || 0;
      return acc;
    },
    { overdueOutstanding: 0, penalty: 0 }
  );

  const font10 = { fontSize: "10px" };
  const inputSmStyle = { ...font10, height: "30px", padding: "2px 6px" };
  const buttonSmStyle = { ...font10, padding: "4px 10px", height: "30px", lineHeight: "1" };

  return (
    <div className="p-3" style={font10}>
      <Form onSubmit={handleSearch} className="mb-3">
        <div className="d-flex align-items-end flex-wrap" style={{ gap: "10px" }}>
          
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Filter Type</Label>
            <Input type="select" bsSize="sm" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={inputSmStyle}>
              <option value="loanId">Loan ID</option>
              <option value="customerId">Customer ID</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">{filterType === "loanId" ? "Loan ID" : "Customer ID"}</Label>
            <Input 
              bsSize="sm" 
              type="text" 
              placeholder={filterType === "loanId" ? "Loan ID" : "Cust ID"} 
              value={filterType === "loanId" ? loanId : customerId} 
              onChange={(e) => filterType === "loanId" ? setLoanId(e.target.value) : setCustomerId(e.target.value)} 
              style={inputSmStyle} 
            />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">From Date</Label>
            <Input type="date" bsSize="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">To Date</Label>
            <Input type="date" bsSize="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <div className="d-flex" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle} disabled={filtering || loading}>
              {loading ? <Spinner size="sm" /> : "Search"}
            </Button>
            
            {/* 👉 Updated Export Button with Spinner */}
            <Button color="success" onClick={exportToCsv} style={buttonSmStyle} disabled={exporting || data.length === 0}>
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-1" /> Generating...
                </>
              ) : (
                "excel"
              )}
            </Button>
          </div>
        </div>
      </Form>

      <hr />

      {error && <Alert color="danger" style={font10}>{error}</Alert>}

      <div style={{ overflowX: "auto" }}>
        <Table bordered striped hover responsive size="sm" className="mb-0" style={{ whiteSpace: "nowrap" }}>
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Loan ID</th>
              <th>Cust ID</th>
              <th>Borrower</th>
              <th>Phone</th>
              <th>Product</th>
              <th>Repayment</th>
              <th>Loan Amt</th>
              <th>Disbursed Amt</th>
              <th>Overdue Outstanding</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>Penalty</th>
              <th>Aging (DPD)</th>
              <th>EMI Count</th>
              <th>EMI Date</th>
              <th>Disbursed Date</th>
              <th>Last Payment</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && !loading ? (
              <tr><td colSpan="19" className="text-center p-3">No records found</td></tr>
            ) : (
              <>
                {data.map((row, i) => (
                  <tr key={i}>
                    <td>{page * PAGE_SIZE + i + 1}</td>
                    <td>{row.loan_id}</td>
                    <td>{row.customer_id}</td>
                    <td>{row.borrower_name}</td>
                    <td>{row.phone_number}</td>
                    <td>{row.product_name}</td>
                    <td>{row.repayment_type}</td>
                    <td>₹{Number(row.loan_amount || 0).toLocaleString()}</td>
                    <td>₹{Number(row.loan_amount || 0).toLocaleString()}</td>
                    <td >₹{Number(row.overdue_outstanding || 0).toLocaleString()}</td>
                    <td>₹{Number(row.overdue_principal || 0).toLocaleString()}</td>
                    <td>₹{Number(row.overdue_interest || 0).toLocaleString()}</td>
                    <td>₹{Number(row.penalty || 0).toLocaleString()}</td>
                    <td className="text-center">{row.aging}</td>
                    <td className="text-center">{row.overdue_emi_count}</td>
                    <td>{row.emi_date}</td>
                    <td>{row.disbursed_date}</td>
                    <td>{row.last_payment_date || "-"}</td>
                    <td>
                      <Button size="sm" color="info" outline style={buttonSmStyle} onClick={() => navigate(`/CustomerView?customer_id=${row.customer_id}`)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}

                <tr className="table-secondary fw-bold">
                  <td colSpan="9" className="text-end">Totals:</td>
                  <td>₹{totals.overdueOutstanding.toLocaleString()}</td>
                  <td colSpan="2"></td>
                  <td>₹{totals.penalty.toLocaleString()}</td>
                  <td colSpan="5"></td>
                </tr>
              </>
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
        <Button size="sm" outline color="primary" style={buttonSmStyle} disabled={page === 0} onClick={() => fetchOverdue(page - 1)}>Prev</Button>
        <span className="fw-bold">Page {page + 1}</span>
        <Button size="sm" outline color="primary" style={buttonSmStyle} disabled={!hasNextPage} onClick={() => fetchOverdue(page + 1)}>Next</Button>
      </div>
    </div>
  );
};

export default OverdueReporting;